import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";
import type { CmsSaveState, CmsSaveStateDetail } from "./types";

export class CmsActionClient {
    constructor(private readonly root: HTMLElement) {}

    async confirmAndSend(url: string, method: string, confirmMsg: string): Promise<boolean> {
        if (!url) return false;
        if (!(await Modal.confirm(confirmMsg))) return false;
        return this.send(url, method);
    }

    async confirmAndSendJson(url: string, method: string, confirmMsg: string): Promise<Record<string, unknown> | null> {
        if (!url) return null;
        if (!(await Modal.confirm(confirmMsg))) return null;
        return this.sendForm(url, { _method: method }, method);
    }

    async restore(url: string): Promise<boolean> {
        if (!url) return false;
        if (!(await Modal.confirm("이 변경을 복구하시겠습니까? 현재 수정 내용이 덮어써질 수 있습니다."))) {
            return false;
        }
        const json = await this.sendForm(url, {}, "POST");
        return json !== null;
    }

    async send(url: string, method: string, payload: Record<string, string | number | boolean | null | undefined> = {}): Promise<boolean> {
        const json = await this.sendForm(url, { _method: method, ...payload }, method);
        return json !== null;
    }

    async sendForm(
        url: string,
        payload: Record<string, string | number | boolean | null | undefined>,
        method = "POST",
        options: { silent?: boolean } = {},
    ): Promise<Record<string, unknown> | null> {
        const csrf = this.csrf();
        const body = new URLSearchParams();
        body.set("_method", method);
        body.set("_token", csrf);
        Object.entries(payload).forEach(([key, value]) => {
            if (value === null || value === undefined) return;
            body.set(key, String(value));
        });
        const toast = options.silent ? null : Toast.loading("저장중...");
        try {
            this.emitSaveState("saving", "저장 중...");
            const res = await fetch(url, {
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": csrf,
                    "X-HTTP-Method-Override": method,
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body,
            });
            const json = (await res.json().catch(() => ({}))) as { result?: string; msg?: string } & Record<string, unknown>;
            if (res.ok && json.result === "success") {
                toast?.success("저장완료");
                this.emitSaveState("saved", "저장됨");
                return json;
            }
            this.emitSaveState("failed", json.msg ?? "저장 실패");
            toast?.error(json.msg ?? "저장 실패");
            return null;
        } catch {
            this.emitSaveState(navigator.onLine ? "failed" : "offline", navigator.onLine ? "네트워크 오류" : "오프라인");
            toast?.error(navigator.onLine ? "네트워크 오류" : "오프라인");
            return null;
        }
    }

    async reorderByMove(target: HTMLElement, direction: "up" | "down"): Promise<boolean> {
        const type = target.dataset.cmsEditableType ?? "";
        const parent = type === "section"
            ? target.closest<HTMLElement>("[data-cms-live-sections]")
            : target.closest<HTMLElement>("[data-cms-live-elements]");
        if (!parent) return false;

        const siblings = Array.from(parent.children)
            .filter((child): child is HTMLElement => {
                return child instanceof HTMLElement && child.dataset.cmsEditableType === type;
            });
        const index = siblings.indexOf(target);
        const swapWith = direction === "up" ? index - 1 : index + 1;
        if (index < 0 || swapWith < 0 || swapWith >= siblings.length) return false;

        if (direction === "up") {
            parent.insertBefore(target, siblings[swapWith]);
        } else {
            parent.insertBefore(siblings[swapWith], target);
        }

        return this.reorderContainer(parent, type);
    }

    async reorderContainer(parent: HTMLElement, type?: string): Promise<boolean> {
        const endpoint = parent.dataset.cmsReorderUrl ?? "";
        if (!endpoint) return false;

        const items = Array.from(parent.children)
            .filter((child): child is HTMLElement => {
                return child instanceof HTMLElement && (!type || child.dataset.cmsEditableType === type);
            })
            .map((child, i) => ({
                id: parseInt(child.dataset.cmsEditableId ?? child.dataset.id ?? "0", 10),
                sort_order: (i + 1) * 10,
            }))
            .filter((item) => item.id > 0);

        if (items.length === 0) return false;

        const csrf = this.csrf();
        document.documentElement.classList.add("admin--loading");
        const toast = Toast.loading("저장중...");
        try {
            this.emitSaveState("saving", "순서 저장 중...");
            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-HTTP-Method-Override": "PATCH",
                    "X-CSRF-TOKEN": csrf,
                },
                body: JSON.stringify({ items, _token: csrf }),
            });
            const json = (await res.json().catch(() => ({}))) as { result?: string; msg?: string };
            if (res.ok && json.result === "success") {
                toast.success("저장완료");
                this.emitSaveState("saved", "순서 저장됨");
                return true;
            }
            this.emitSaveState("failed", json.msg ?? "순서 저장 실패");
            toast.error(json.msg ?? "순서 저장 실패");
            return false;
        } catch {
            this.emitSaveState(navigator.onLine ? "failed" : "offline", navigator.onLine ? "순서 저장 네트워크 오류" : "오프라인");
            toast.error(navigator.onLine ? "순서 저장 네트워크 오류" : "오프라인");
            return false;
        } finally {
            document.documentElement.classList.remove("admin--loading");
        }
    }

    csrf(): string {
        return (
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
            this.root.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
            ""
        );
    }

    private emitSaveState(state: CmsSaveState, message?: string): void {
        this.root.dispatchEvent(new CustomEvent<CmsSaveStateDetail>("cms:save-state", {
            bubbles: true,
            detail: { state, message },
        }));
    }
}
